'use client';

import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGSAP } from '@gsap/react';
import initAnimations from './animations/batch-generate-animation';
import { useLocale } from '@/components/providers/context/locale-context';

function BatchGenerate() {
    const { t } = useTranslation('batch-generate');
    const locale = useLocale();
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const batchImageContainerRef = useRef<HTMLDivElement>(null);
    const batchImagesRef = useRef<HTMLCanvasElement>(null);
    const batchTextRef = useRef<HTMLDivElement>(null);

    useGSAP(
        () => {
            return initAnimations(
                rootRef.current!,
                inputRef.current!,
                t('input.text'),
                buttonRef.current!,
                batchImageContainerRef.current!,
                batchImagesRef.current!,
                batchTextRef.current!,
            );
        },
        { scope: rootRef },
    );

    useEffect(() => {
        if (buttonRef.current) {
            buttonRef.current.classList.toggle('disabled');
        }
    }, []); // 确保只在组件首次渲染时执行

    return (
        <section
            id="batch-generate"
            ref={rootRef}
            style={{ height: 'var(--simulaix-vh, 100svh)' }}
            className="relative flex w-full max-w-full flex-col bg-black"
        >
            <div
                id="batch-generate-image"
                ref={batchImageContainerRef}
                className="z-0 h-full w-full"
            >
                <canvas ref={batchImagesRef} className="h-full w-full" />
            </div>

            <div
                id="batch-generate-input"
                className={`absolute left-0 top-0 z-20 flex h-full w-full max-w-full items-center justify-center`}
            >
                <div className={clsx(`w-full sm:w-4/5`)}>
                    <div className="relative flex w-full max-w-full items-center justify-center px-[20px] sm:px-0">
                        <div
                            ref={inputRef}
                            style={{
                                opacity: 'var(--batch-input-opacity)',
                                border: '1px solid transparent',
                                backgroundClip: 'padding-box, border-box',
                                backgroundOrigin: 'padding-box, border-box',
                                backgroundImage:
                                    'linear-gradient(to right, #000000, #000000), linear-gradient(90deg, #c1b1ed 0%, #7f62d3 23.5%, #537cf7 53.15%, #e356fd 77.87%, #fbde7a 100%)',
                            }}
                            className="relative flex max-h-[132px] min-h-[132px] w-full flex-row items-start rounded-[24px] bg-black shadow-lg shadow-[#5e5e5a] sm:max-h-[62px] sm:min-h-[62px] sm:w-[760px] sm:items-center sm:rounded-[83px]"
                        >
                            <div
                                id="batch-generate-input-text"
                                className={clsx(
                                    'j-t1 font-inter h-full px-[16px] py-[6px] text-left text-white sm:px-[24px] sm:py-[19px]',
                                )}
                            ></div>
                            <button
                                ref={buttonRef}
                                className={
                                    'absolute bottom-[10px] right-[10px] h-[32px] rounded-[39px] bg-[#6038FF] px-[18px] sm:h-[38px]'
                                }
                            >
                                <div className="flex flex-row items-center justify-between gap-[4px] sm:gap-[8px]">
                                    <svg
                                        className="h-[13px] w-[13px] sm:h-[26px] sm:w-[26px]"
                                        viewBox="0 0 26 26"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M24.0067 6.27273L21.7642 7.29106L20.7567 9.52273C20.723 9.64044 20.652 9.744 20.5542 9.81773C20.4565 9.89145 20.3374 9.93133 20.215 9.93133C20.0926 9.93133 19.9735 9.89145 19.8757 9.81773C19.778 9.744 19.7069 9.64044 19.6733 9.52273L18.655 7.29106L16.4125 6.27273C16.3166 6.22526 16.2358 6.15192 16.1794 6.06099C16.1229 5.97006 16.093 5.86517 16.093 5.75815C16.093 5.65112 16.1229 5.54623 16.1794 5.4553C16.2358 5.36438 16.3166 5.29104 16.4125 5.24356L18.655 4.23606L19.6733 1.99356C19.7069 1.87585 19.778 1.77229 19.8757 1.69857C19.9735 1.62484 20.0926 1.58496 20.215 1.58496C20.3374 1.58496 20.4565 1.62484 20.5542 1.69857C20.652 1.77229 20.723 1.87585 20.7567 1.99356L21.7642 4.23606L24.0067 5.24356C24.1061 5.28787 24.1906 5.36006 24.2499 5.45138C24.3092 5.54271 24.3408 5.64926 24.3408 5.75815C24.3408 5.86703 24.3092 5.97359 24.2499 6.06491C24.1906 6.15624 24.1061 6.22842 24.0067 6.27273ZM16.5425 13.9319L12.3175 15.8602L10.3892 20.0852C10.2993 20.2655 10.161 20.4172 9.98971 20.5232C9.81843 20.6292 9.621 20.6853 9.41957 20.6853C9.21815 20.6853 9.02071 20.6292 8.84944 20.5232C8.67817 20.4172 8.53984 20.2655 8.44999 20.0852L6.49999 15.8602L2.26416 13.9319C2.08388 13.842 1.93223 13.7037 1.82622 13.5324C1.72022 13.3612 1.66406 13.1637 1.66406 12.9623C1.66406 12.7609 1.72022 12.5635 1.82622 12.3922C1.93223 12.2209 2.08388 12.0826 2.26416 11.9927L6.49999 10.0644L8.42832 5.8394C8.51817 5.65912 8.6565 5.50747 8.82777 5.40146C8.99905 5.29546 9.19648 5.2393 9.39791 5.2393C9.59933 5.2393 9.79677 5.29546 9.96804 5.40146C10.1393 5.50747 10.2776 5.65912 10.3675 5.8394L12.2958 10.0644L16.5208 11.9927C16.7021 12.0805 16.8553 12.2172 16.9633 12.3873C17.0712 12.5574 17.1296 12.7542 17.1318 12.9556C17.1341 13.157 17.0801 13.3551 16.976 13.5276C16.8719 13.7 16.7218 13.8401 16.5425 13.9319ZM20.0742 22.0677L22.0458 21.1686C22.1328 21.1273 22.2057 21.0613 22.2552 20.9787C22.3047 20.8961 22.3287 20.8007 22.3241 20.7045C22.3194 20.6083 22.2864 20.5157 22.2292 20.4383C22.172 20.3608 22.0931 20.3021 22.0025 20.2694L20.0308 19.3702L19.1317 17.3986C19.0924 17.3107 19.0286 17.2361 18.9478 17.1837C18.8671 17.1313 18.7729 17.1035 18.6767 17.1035C18.5804 17.1035 18.4862 17.1313 18.4055 17.1837C18.3248 17.2361 18.2609 17.3107 18.2217 17.3986L17.3333 19.3702L15.3508 20.2694C15.2622 20.3065 15.1866 20.369 15.1334 20.449C15.0802 20.529 15.0518 20.6229 15.0518 20.719C15.0518 20.815 15.0802 20.909 15.1334 20.9889C15.1866 21.0689 15.2622 21.1314 15.3508 21.1686L17.3333 22.0677L18.265 24.0394C18.3042 24.1273 18.3681 24.2019 18.4488 24.2543C18.5296 24.3066 18.6238 24.3345 18.72 24.3345C18.8162 24.3345 18.9104 24.3066 18.9911 24.2543C19.0719 24.2019 19.1357 24.1273 19.175 24.0394L20.0742 22.0677Z"
                                            fill="white"
                                            fillOpacity="0.8"
                                        />
                                    </svg>

                                    <div className={clsx('j-t1 text-white')}>
                                        {t('input.button')}
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div
                id="batch-generate-image-text"
                ref={batchTextRef}
                className="absolute left-0 top-0 z-20 h-[var(--simulaix-vh,100svh)] w-full"
            >
                <h2
                    className={clsx(
                        'j-h1 flex h-full w-full items-center justify-center text-center text-white',
                    )}
                >
                    {t('image.firstLine')}
                    <span className="hidden">
                        {locale === 'en' ? ',' : '，'}
                    </span>
                    <br />
                    {t('image.secondLine')}
                </h2>
            </div>
        </section>
    );
}

export default BatchGenerate;
